import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaginatedApi, useFormSubmit, useAsyncOperation, useApi } from '../hooks/useApi';
import { transporteursAPI, clientsAPI, trucksAPI, systemConfigAPI } from '../services/api';
import { CLIENT_FLEET_MAINTENANCE_CONFIG_KEY } from '../constants/systemConfigKeys';
import MultiSelect from '../components/MultiSelect';
import SearchableSelect from '../components/SearchableSelect';
import { formatDate } from '../utils/helpers';
import Table from '../components/Table';
import Button from '../components/Button';
import Input from '../components/Input';
import Modal, { ConfirmModal } from '../components/Modal';
import { TruckIcon, PlusIcon, EyeIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

const isSystemGeneratedTransporteurEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  const normalized = email.trim().toLowerCase();
  return (
    normalized.includes('@ponctuel.iportcloud.invalid') ||
    normalized.includes('@prep.iportcloud.invalid')
  );
};

const TransporteursPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isManagerOrAdmin, isOperator } = useAuth();
  const [search, setSearch] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isTrucksModalOpen, setIsTrucksModalOpen] = useState(false);
  const [selectedTransporteur, setSelectedTransporteur] = useState(null);
  const [clientFilter, setClientFilter] = useState('');
  const searchTimeoutRef = useRef(null);

  const { data: clientsData } = useApi(() => clientsAPI.getAll({ limit: 500 }));
  const clients = clientsData?.clients || [];
  const { data: clientFleetMaintenanceConfig } = useApi(
    isOperator() ? () => systemConfigAPI.getByKey(CLIENT_FLEET_MAINTENANCE_CONFIG_KEY) : null
  );
  const clientFleetMaintenanceEnabled = Boolean(clientFleetMaintenanceConfig?.value ?? clientFleetMaintenanceConfig?.data?.value);
  const canMaintainTransporteurs = isManagerOrAdmin() || (isOperator() && clientFleetMaintenanceEnabled);

  const clientFilterOptions = useMemo(
    () => [
      { value: '', label: t('transporteurs.allClients') },
      ...clients.map((c) => ({
        value: c.id,
        label: c.companyName || c.id,
        searchText: [c.companyName, c.contactName, c.contactPhone, c.contactEmail].filter(Boolean).join(' ')
      }))
    ],
    [clients, t]
  );

  const {
    data: transporteurs,
    pagination,
    loading,
    updateParams,
    goToPage,
    refresh
  } = usePaginatedApi(transporteursAPI.getAll, { search, clientId: clientFilter || undefined });

  const { submit: submitCreate, loading: createLoading } = useFormSubmit(
    transporteursAPI.create,
    {
      onSuccess: () => {
        setIsAddModalOpen(false);
        refresh();
        toast.success(t('transporteurs.created'));
      },
    }
  );

  const { submit: submitUpdate, loading: updateLoading } = useFormSubmit(
    (data) => transporteursAPI.update(selectedTransporteur.id, data),
    {
      onSuccess: () => {
        setIsEditModalOpen(false);
        setSelectedTransporteur(null);
        refresh();
        toast.success(t('transporteurs.updated'));
      },
    }
  );

  const { execute, loading: deleteLoading } = useAsyncOperation();

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => updateParams({ search, clientId: clientFilter || undefined }), 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [search, clientFilter, updateParams]);

  const handleView = (row) => {
    setSelectedTransporteur(row);
    setIsViewModalOpen(true);
  };

  const handleEdit = (row) => {
    setSelectedTransporteur(row);
    setIsEditModalOpen(true);
  };

  const handleDelete = (row) => {
    setSelectedTransporteur(row);
    setIsDeleteModalOpen(true);
  };

  const handleShowTrucks = (row) => {
    setSelectedTransporteur(row);
    setIsTrucksModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedTransporteur) return;
    try {
      await execute(
        () => transporteursAPI.delete(selectedTransporteur.id),
        { showSuccessToast: true, successMessage: t('transporteurs.deleted') }
      );
      setIsDeleteModalOpen(false);
      setSelectedTransporteur(null);
      refresh();
    } catch (_) {}
  };

  const columns = [
    {
      key: 'companyName',
      title: t('transporteurs.companyName'),
      render: (value) => <span className="font-medium text-gray-900">{value}</span>,
    },
    {
      key: 'contactName',
      title: t('transporteurs.contactName'),
      render: (value, row) => (
        <div>
          <div className="text-sm text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">
            {row.contactEmail && !isSystemGeneratedTransporteurEmail(row.contactEmail)
              ? row.contactEmail
              : '—'}
          </div>
          <div className="text-sm text-gray-500">{row.contactPhone}</div>
        </div>
      ),
    },
    {
      key: 'status',
      title: t('transporteurs.status'),
      render: (value) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
          value === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {t(`transporteurs.${value}`)}
        </span>
      ),
    },
    {
      key: '_count',
      title: t('transporteurs.trucks.column'),
      render: (_, row) => (
        <span className="text-sm text-gray-600">{row._count?.truckTransporteurs ?? 0}</span>
      ),
    },
    {
      key: 'loadsViaTrucks',
      title: t('transporteurs.loadsCount'),
      render: (_, row) => {
        const n = row.loadsViaTrucks ?? 0;
        return (
          <span className={`text-sm ${n > 0 ? 'text-amber-600 font-medium' : 'text-gray-600'}`}>
            {n}
          </span>
        );
      },
    },
    {
      key: 'clients',
      title: t('transporteurs.clients'),
      render: (_, row) => (
        <span className="text-sm text-gray-600">
          {row.clients?.length ? row.clients.map(c => c.companyName).join(', ') : '—'}
        </span>
      ),
    },
    {
      key: 'address',
      title: t('transporteurs.address'),
      render: (value) => <span className="text-sm text-gray-600 line-clamp-2">{value}</span>,
    },
    {
      key: 'actions',
      title: t('tables.actions'),
      sticky: true,
      width: '220px',
      minWidth: 220,
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={() => handleView(row)} title={t('common.view')}>
            <EyeIcon className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => handleShowTrucks(row)} title={t('transporteurs.trucks.views')} className="relative">
            <TruckIcon className="h-4 w-4" />
            {row._count?.truckTransporteurs > 0 && (
              <span className="absolute top-1 right-1 bg-blue-600 text-white text-[10px] font-bold rounded-full px-1 py-0.5 flex items-center justify-center" style={{ minWidth: '14px', height: '14px', lineHeight: '14px' }}>
                {row._count.truckTransporteurs}
              </span>
            )}
          </Button>
          {canMaintainTransporteurs && (
            <Button size="sm" variant="ghost" onClick={() => handleEdit(row)} title={t('common.edit')}>
              <PencilIcon className="h-4 w-4" />
            </Button>
          )}
          {canMaintainTransporteurs && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDelete(row)}
              title={row.canDelete !== true ? t('transporteurs.cannotDeleteWithLoads') : t('common.delete')}
              className={row.canDelete !== true ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-700'}
              disabled={row.canDelete !== true}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <TruckIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('transporteurs.title')}</h1>
            <p className="text-sm sm:text-base text-gray-600 hidden sm:block">{t('transporteurs.subtitle')}</p>
          </div>
        </div>
        {canMaintainTransporteurs && (
          <Button onClick={() => setIsAddModalOpen(true)}>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t('transporteurs.addTransporteur')}
          </Button>
        )}
      </div>

      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <Input
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1"
          />
          <div className="w-full sm:w-72 min-w-0">
            <SearchableSelect
              name="clientFilter"
              label={t('transporteurs.filterByClient')}
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              options={clientFilterOptions}
              placeholder={t('transporteurs.filterByClientPlaceholder')}
            />
          </div>
        </div>
        <Table
          columns={columns}
          data={transporteurs}
          loading={loading}
          pagination={pagination}
          onPageChange={goToPage}
          emptyMessage={t('transporteurs.noTransporteursFound')}
        />
      </div>

      <TransporteurForm
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={submitCreate}
        loading={createLoading}
        clients={clients}
        title={t('transporteurs.newTransporteur')}
        submitText={t('common.create')}
      />
      <TransporteurForm
        isOpen={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setSelectedTransporteur(null); }}
        onSubmit={submitUpdate}
        loading={updateLoading}
        clients={clients}
        title={t('transporteurs.editTransporteur')}
        submitText={t('common.update')}
        initialData={selectedTransporteur}
      />
      <ViewTransporteurModal
        isOpen={isViewModalOpen}
        onClose={() => { setIsViewModalOpen(false); setSelectedTransporteur(null); }}
        transporteur={selectedTransporteur}
      />
      <TransporteurTrucksModal
        isOpen={isTrucksModalOpen}
        onClose={() => { setIsTrucksModalOpen(false); setSelectedTransporteur(null); }}
        transporteur={selectedTransporteur}
      />
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setSelectedTransporteur(null); }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        title={t('transporteurs.deleteTransporteur')}
        message={t('transporteurs.confirmDelete', { name: selectedTransporteur?.companyName })}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};

const emptyTransporteurForm = () => ({
  companyName: '',
  address: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  status: 'active',
  clientIds: [],
  truckIds: [],
});

const TransporteurForm = ({ isOpen, onClose, onSubmit, loading, clients = [], title, submitText, initialData = {} }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(emptyTransporteurForm);
  const [allTrucks, setAllTrucks] = useState([]);
  const [trucksLoading, setTrucksLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setAllTrucks([]);
      setFormData(emptyTransporteurForm());
      return undefined;
    }

    setTrucksLoading(true);
    trucksAPI
      .getAll({ limit: 500 })
      .then((res) => setAllTrucks(res.data?.trucks || []))
      .catch(() => setAllTrucks([]))
      .finally(() => setTrucksLoading(false));

    setFormData({
      companyName: initialData?.companyName || '',
      address: initialData?.address || '',
      contactName: initialData?.contactName || '',
      contactEmail: initialData?.contactEmail || '',
      contactPhone: initialData?.contactPhone || '',
      status: initialData?.status || 'active',
      clientIds: initialData?.clients?.map((c) => c.id) || [],
      truckIds: [],
    });

    if (!initialData?.id) {
      return undefined;
    }

    let cancelled = false;
    transporteursAPI
      .getById(initialData.id)
      .then((res) => {
        if (cancelled) return;
        const tr = res.data?.transporteur;
        const ids = (tr?.trucks || []).map((truck) => truck.id).filter(Boolean);
        setFormData((prev) => ({ ...prev, truckIds: ids }));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [isOpen, initialData?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- initialize form values when opening or switching the edited transporteur

  const truckOptions = useMemo(
    () =>
      allTrucks.map((truck) => ({
        value: truck.id,
        label: `${truck.plateNumber}${truck.status === 'inactive' ? ` (${t('trucks.inactive')})` : ''}`,
      })),
    [allTrucks, t]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.contactName || !formData.contactEmail || !formData.address || !formData.contactPhone) {
      return;
    }
    onSubmit({
      ...formData,
      clientIds: formData.clientIds || [],
      truckIds: formData.truckIds || [],
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const clientOptions = clients.map(c => ({ value: c.id, label: c.companyName }));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg" closeOnOverlayClick={false}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>{t('common.cancel')}</Button>
          <Button type="submit" loading={loading} form="transporteur-form">{submitText}</Button>
        </>
      }
    >
      <form id="transporteur-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input label={t('transporteurs.companyName')} name="companyName" value={formData.companyName} onChange={handleChange} required />
          </div>
          <div className="md:col-span-2">
            <Input label={t('transporteurs.address')} name="address" value={formData.address} onChange={handleChange} required />
          </div>
          <Input label={t('transporteurs.contactName')} name="contactName" value={formData.contactName} onChange={handleChange} required />
          <Input label={t('transporteurs.contactEmail')} name="contactEmail" type="email" value={formData.contactEmail} onChange={handleChange} required />
          <Input label={t('transporteurs.contactPhone')} name="contactPhone" value={formData.contactPhone} onChange={handleChange} required />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('transporteurs.status')}</label>
            <select name="status" value={formData.status} onChange={handleChange} className="input w-full">
              <option value="active">{t('transporteurs.active')}</option>
              <option value="inactive">{t('transporteurs.inactive')}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <MultiSelect
              label={t('transporteurs.clientsCanWorkWith')}
              name="clientIds"
              values={formData.clientIds || []}
              onChange={handleChange}
              options={clientOptions}
              placeholder={t('transporteurs.selectClients')}
            />
          </div>
          <div className="md:col-span-2">
            <MultiSelect
              label={t('transporteurs.linkedTrucks')}
              name="truckIds"
              values={formData.truckIds || []}
              onChange={handleChange}
              options={truckOptions}
              placeholder={trucksLoading ? t('common.loading') : t('transporteurs.selectLinkedTrucks')}
              disabled={trucksLoading}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

const ViewTransporteurModal = ({ isOpen, onClose, transporteur }) => {
  const { t } = useTranslation();
  if (!transporteur) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose} title={transporteur.companyName} footer={<Button onClick={onClose}>{t('common.close')}</Button>}>
      <div className="space-y-3">
        <div><span className="text-sm font-medium text-gray-500">{t('transporteurs.address')}</span><p className="text-gray-900">{transporteur.address}</p></div>
        <div><span className="text-sm font-medium text-gray-500">{t('transporteurs.contactName')}</span><p className="text-gray-900">{transporteur.contactName}</p></div>
        <div>
          <span className="text-sm font-medium text-gray-500">{t('transporteurs.contactEmail')}</span>
          <p className="text-gray-900">
            {transporteur.contactEmail && !isSystemGeneratedTransporteurEmail(transporteur.contactEmail)
              ? transporteur.contactEmail
              : '—'}
          </p>
        </div>
        <div><span className="text-sm font-medium text-gray-500">{t('transporteurs.contactPhone')}</span><p className="text-gray-900">{transporteur.contactPhone}</p></div>
        <div><span className="text-sm font-medium text-gray-500">{t('transporteurs.status')}</span><p className="text-gray-900">{t(`transporteurs.${transporteur.status}`)}</p></div>
        <div>
          <span className="text-sm font-medium text-gray-500">{t('transporteurs.clients')}</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {transporteur.clients?.length ? (
              transporteur.clients.map((c, idx) => (
                <span key={c.id || idx} className="bg-blue-100 text-blue-800 px-2 py-0.5 rounded text-xs font-medium">
                  {c.companyName}
                </span>
              ))
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </div>
        </div>
        {transporteur.createdAt && <div><span className="text-sm font-medium text-gray-500">{t('tables.date')}</span><p className="text-gray-900">{formatDate(transporteur.createdAt)}</p></div>}
      </div>
    </Modal>
  );
};

const TransporteurTrucksModal = ({ isOpen, onClose, transporteur }) => {
  const { t } = useTranslation();
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && transporteur?.id) {
      setLoading(true);
      trucksAPI.getAll({ transporteurId: transporteur.id, limit: 500 })
        .then((response) => {
          setTrucks(response.data?.trucks || []);
        })
        .catch((error) => {
          console.error('Error fetching trucks:', error);
          setTrucks([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, transporteur?.id]);

  if (!transporteur) return null;

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title={`${t('transporteurs.trucksFor')} ${transporteur.companyName}`}
      size="lg"
      footer={<Button onClick={onClose}>{t('common.close')}</Button>}
    >
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : trucks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <TruckIcon className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p>{t('transporteurs.noTrucksFound')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {trucks.map((truck) => (
            <div key={truck.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <TruckIcon className="h-5 w-5 text-blue-600" />
                    <h3 className="font-semibold text-gray-900">{truck.plateNumber}</h3>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      truck.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {truck.status === 'active' ? t('trucks.active') : t('trucks.inactive')}
                    </span>
                    <span className="ml-2 text-xs text-gray-500">{t('trucks.totalLoads')}: {truck._count?.loads ?? 0}</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    {truck.drivers && truck.drivers.length > 0 ? (
                      <div>
                        <span className="text-gray-600">{t('trucks.drivers')}:</span>
                        <div className="ml-4 mt-1 space-y-1">
                          {truck.drivers.map((driver, idx) => (
                            <div key={idx} className="text-gray-900">
                              • {driver.firstName} {driver.lastName}
                              {driver.phone && <span className="text-gray-500 ml-2">({driver.phone})</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 italic">{t('trucks.noDriversAssigned')}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default TransporteursPage;
